import { useMemo } from 'react';
import { useTenant } from '@/core/tenant/TenantContext';
import { useActiveContext } from '@/shared/hooks/useActiveContext';
import { useLocations } from '@/entities/location';
import { cn } from '@/lib/utils';

export interface GetawaySwitcherProps {
  current: 'location' | 'company';
  /** Whether the sidebar is expanded (full labels) or collapsed (thumb only). */
  expanded: boolean;
  /** Optional class added to the surface. */
  className?: string;
}

/**
 * GetawaySwitcher — header label of the active scope. Plain text showing the
 * active location/company NAME. It is NOT a button and carries no scope
 * indicator of its own (the scope is shown once by ScopeBadge). Scope
 * switching lives in the user menu (UserMenu → Switch context → /select).
 */
export function GetawaySwitcher({ current, expanded, className }: GetawaySwitcherProps) {
  const { companyName, accessibleLocationIds, isCompanyLevel } = useTenant();
  const { activeContext } = useActiveContext();
  const { data: locations = [] } = useLocations();

  const accessibleLocations = useMemo(() => {
    if (isCompanyLevel) return locations;
    const allowed = new Set(accessibleLocationIds);
    return locations.filter((loc) => allowed.has(loc.id));
  }, [locations, accessibleLocationIds, isCompanyLevel]);

  const activeLocation = useMemo(() => {
    if (current !== 'location') return null;
    return accessibleLocations.find((l) => l.id === activeContext?.id) ?? null;
  }, [current, accessibleLocations, activeContext?.id]);

  const displayName = current === 'company' ? companyName || '…' : activeLocation?.name ?? '…';

  if (!expanded) return null;

  return (
    <span
      className={cn('truncate font-serif text-sm font-semibold text-sand-900', className)}
      title={displayName}
    >
      {displayName}
    </span>
  );
}

export default GetawaySwitcher;
